import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import i18n from "../../../i18n";
//import { Trans } from "react-i18next";
import { AppLayout, PageBackLink } from '../../../components/layout';
import { LeaveActionToast } from '../../leave-management/components/LeaveActionToast';
import '../inventory.css';
import {
  SUPPLIER_TYPES,
  loadVendorCatalog,
  fetchVendorCount,
} from '../../../services/productVendorService';

const TOAST_KEY = 'product_vendor_toast';
const PAGE_SIZE = 10;

function translateVendorStatus(status) {
  if (status === 'Active') return i18n.t('status.active');
  if (status === 'Inactive') return i18n.t('status.inactive');
  if (status === 'Blacklisted') return i18n.t('inventory.blacklisted');
  return status;
}

function translateRiskLevelLabel(riskLevel) {
  if (riskLevel === 'Low') return i18n.t('inventory.lowRisk');
  if (riskLevel === 'Medium') return i18n.t('inventory.mediumRisk');
  if (riskLevel === 'High') return i18n.t('inventory.highRisk');
  return riskLevel ? i18n.t('inventory.riskLevelWithValue', { riskLevel }) : riskLevel;
}

function translateSupplierType(type) {
  if (type === 'Manufacturer') return i18n.t('crm.manufacturer');
  if (type === 'OEM') return i18n.t('inventory.oem');
  if (type === 'Distributor') return i18n.t('inventory.distributor');
  return type;
}

function VendorStatusBadge({ status }) {
  const isBlacklisted = status === 'Blacklisted';
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap"
      style={{
        backgroundColor: status === 'Active' ? '#ecfdf5' : isBlacklisted ? '#fee2e2' : '#f1f5f9',
        color: status === 'Active' ? '#065f46' : isBlacklisted ? '#991b1b' : '#475569',
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
      }}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {translateVendorStatus(status)}
    </span>
  );
}

function VendorRiskBadge({ riskLevel }) {
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold"
      style={{
        backgroundColor: riskLevel === 'Low' ? '#e0f2fe' : riskLevel === 'Medium' ? '#fef3c7' : '#ffedd5',
        color: riskLevel === 'Low' ? '#0369a1' : riskLevel === 'Medium' ? '#b45309' : '#c2410c',
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
      }}
    >
      {translateRiskLevelLabel(riskLevel)}
    </span>
  );
}

function VendorMobileCard({ vendor, onSelect, onEdit }) {
  const isBlacklisted = vendor.status === 'Blacklisted';

  return (
    <div
      className="p-4 space-y-3 active:bg-[var(--color-workspace)]/40 transition cursor-pointer"
      style={{ backgroundColor: isBlacklisted ? '#fef2f2' : undefined }}
      onClick={() => onSelect(vendor)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(vendor);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="flex justify-between items-start gap-2">
        <h4 className="font-semibold text-foreground leading-snug">{vendor.tradeName}</h4>
        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--color-primary)]/10 text-[var(--color-primary)] whitespace-nowrap">
          {vendor.type ? translateSupplierType(vendor.type) : i18n.t('inventory.emDash')}
        </span>
      </div>

      <div className="text-xs space-y-1 text-[var(--color-text-secondary)]">
        <div className="flex justify-between gap-3">
          <span>{i18n.t('inventory.riskLevelLabel')}:</span>
          <VendorRiskBadge riskLevel={vendor.riskLevel} />
        </div>
      </div>

      <div className="flex justify-between items-center pt-2" onClick={(e) => e.stopPropagation()}>
        <VendorStatusBadge status={vendor.status} />
        <button
          type="button"
          className="text-[13px] font-semibold text-[var(--color-primary)] cursor-pointer bg-transparent border-0 p-0"
          onClick={() => onEdit(vendor)}
        >
          {i18n.t('common.edit')}
        </button>
      </div>
    </div>
  );
}

export default function VendorCatalog() {
  const navigate = useNavigate();

  const [vendors, setVendors] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [showFilters, setShowFilters] = useState(false);
  const [filterType, setFilterType] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterRisk, setFilterRisk] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const raw = sessionStorage.getItem(TOAST_KEY);
    if (!raw) return;
    try {
      setToast(JSON.parse(raw));
    } catch {
      setToast(null);
    }
    sessionStorage.removeItem(TOAST_KEY);
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [filterType, filterStatus, filterRisk]);

  const loadVendors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await loadVendorCatalog({
        searchQuery: debouncedSearch,
        filterType,
        filterStatus,
        filterRisk,
        page,
        size: PAGE_SIZE,
      });
      setVendors(result.vendors || []);
      setTotalElements(result.totalElements ?? (result.vendors || []).length);
      setTotalPages(
        result.totalPages ??
          Math.max(1, Math.ceil((result.totalElements ?? (result.vendors || []).length) / PAGE_SIZE))
      );
    } catch (err) {
      setVendors([]);
      setTotalElements(0);
      setTotalPages(0);
      setError(err?.message || i18n.t('inventory.failedToLoadVendors'));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filterType, filterStatus, filterRisk, page]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  useEffect(() => {
    let cancelled = false;
    fetchVendorCount()
      .then((count) => {
        if (!cancelled) setTotalCount(count);
      })
      .catch(() => {
        if (!cancelled) setTotalCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [vendors]);

  const onAddVendor = () => navigate('/IMS/inventory/vendors/new');
  // const onEditVendor = (vendor) => navigate(`/IMS/inventory/vendors/${vendor.id}/edit`);
  const onEditVendor = (vendor) =>
  navigate(`/IMS/inventory/vendors/${vendor.id}/edit`, {
    state: {
      from: 'catalog',
    },
  });
  const onRowClick = (vendor) => navigate(`/IMS/inventory/vendors/${vendor.id}`);
  const onResetFilters = () => {
    setFilterType('ALL');
    setFilterStatus('ALL');
    setFilterRisk('ALL');
    setSearchQuery('');
    setPage(0);
  };

  const isFiltersActive =
    filterType !== 'ALL' || filterStatus !== 'ALL' || filterRisk !== 'ALL' || searchQuery !== '';

  const hasNoVendorsGlobal =
    !loading &&
    !error &&
    vendors.length === 0 &&
    !isFiltersActive &&
    page === 0 &&
    (totalCount === 0 || totalCount === null);

  const hasNoMatch = !loading && !error && vendors.length === 0 && !hasNoVendorsGlobal;

  return (
    <AppLayout overlay={<LeaveActionToast toast={toast} />}>
      <div className="inventory-module">
    <div style={{ padding: '0 8px' }}>
      
      <div className="page-header-responsive" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <PageBackLink to="/" />
          <h1 className="page-title">{i18n.t('inventory.vendorCatalogTitle')}</h1>
        </div>
        <button 
          type="button" 
          className="btn-primary" 
          onClick={onAddVendor}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '40px', borderRadius: '8px', padding: '0 20px', fontWeight: '600' }}
        >
          <Plus size={18} />
          <span>{i18n.t('inventory.addVendor')}</span>
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: '16px', padding: '14px 16px', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#991b1b', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '14px', fontWeight: '600' }}>{error}</span>
          <button type="button" className="btn-secondary" style={{ height: '32px', fontSize: '13px' }} onClick={loadVendors}>
            {i18n.t('attendance.retry')}
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ padding: '48px 20px', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} style={{ height: '44px', borderRadius: '8px', backgroundColor: '#f1f5f9' }} />
            ))}
          </div>
        </div>
      ) : hasNoVendorsGlobal ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#f0fdf4', color: 'var(--accent-green)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
            <Plus size={32} />
          </div>
          <h3 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--primary-navy)', marginBottom: '8px', fontFamily: 'var(--font-display)' }}>{i18n.t('inventory.noVendorsRegisteredYet')}</h3>
          <p style={{ color: 'var(--text-grey)', fontSize: '14px', maxWidth: '400px', marginBottom: '24px', lineHeight: '1.5' }}>
            {i18n.t('inventory.noVendorsRegisteredHint')}
          </p>
          <button 
            type="button" 
            className="btn-primary" 
            onClick={onAddVendor}
            style={{ height: '40px', borderRadius: '8px', padding: '0 20px', fontWeight: '600' }}
          >
            {i18n.t('inventory.addVendor')}
          </button>
        </div>
      ) : (
        <>
          <div className="filters-card-clean inv-filter-panel" style={{ marginBottom: '8px', flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
            <div className="filter-search-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap', width: '100%' }}>
              <div className="filter-search-wrapper" style={{ flex: '1 1 auto', minWidth: 0, maxWidth: '360px' }}>
                <Search className="filter-search-icon" size={16} />
                <input 
                  type="text" 
                  className="filter-search-input" 
                  placeholder={i18n.t('inventory.searchVendorsPlaceholder')} 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  height: '40px',
                  padding: '0 14px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)',
                  backgroundColor: showFilters || (filterType !== 'ALL' || filterStatus !== 'ALL' || filterRisk !== 'ALL') ? '#eff6ff' : '#ffffff',
                  color: showFilters || (filterType !== 'ALL' || filterStatus !== 'ALL' || filterRisk !== 'ALL') ? 'var(--accent-blue)' : 'var(--text-dark)',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  transition: 'var(--transition-all)'
                }}
              >
                <Filter size={16} />
                <span>{i18n.t('formActions.filter')}</span>
                {(filterType !== 'ALL' || filterStatus !== 'ALL' || filterRisk !== 'ALL') && (
                  <span style={{
                    backgroundColor: 'var(--accent-blue)',
                    color: '#ffffff',
                    fontSize: '10px',
                    fontWeight: '700',
                    borderRadius: '50%',
                    width: '16px',
                    height: '16px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: '2px'
                  }}>
                    !
                  </span>
                )}
              </button>
            </div>

            {showFilters && (
              <div className="inv-filter-fields" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', width: '100%', paddingTop: '4px' }}>
                <select 
                  className="filter-select" 
                  value={filterType} 
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="ALL">{i18n.t('inventory.allVendorTypes')}</option>
                  {SUPPLIER_TYPES.map((t) => (
                    <option key={t.id} value={t.label}>{translateSupplierType(t.label)}</option>
                  ))}
                </select>

                <select 
                  className="filter-select" 
                  value={filterStatus} 
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="ALL">{i18n.t('common.allStatuses')}</option>
                  <option value="Active">{i18n.t('status.active')}</option>
                  <option value="Inactive">{i18n.t('status.inactive')}</option>
                  <option value="Blacklisted">{i18n.t('inventory.blacklisted')}</option>
                </select>

                <select 
                  className="filter-select" 
                  value={filterRisk} 
                  onChange={(e) => setFilterRisk(e.target.value)}
                >
                  <option value="ALL">{i18n.t('inventory.allRiskLevels')}</option>
                  <option value="Low">{i18n.t('inventory.lowRisk')}</option>
                  <option value="Medium">{i18n.t('inventory.mediumRisk')}</option>
                  <option value="High">{i18n.t('inventory.highRisk')}</option>
                </select>

                {isFiltersActive && (
                  <button 
                    type="button" 
                    className="btn-secondary" 
                    onClick={onResetFilters}
                    style={{ height: '40px', padding: '0 16px', fontSize: '13px', fontWeight: '600', color: 'var(--accent-red)', borderColor: 'rgba(239,68,68,0.2)' }}
                  >
                    {i18n.t('formActions.clearFilters')}
                  </button>
                )}
              </div>
            )}
          </div>

          {hasNoMatch ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color)', textAlign: 'center', marginTop: '12px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#f3f4f6', color: 'var(--text-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                <Filter size={24} />
              </div>
              <h4 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--primary-navy)', marginBottom: '4px' }}>{i18n.t('inventory.noSuppliersMatchSearch')}</h4>
              <p style={{ color: 'var(--text-grey)', fontSize: '13px', maxWidth: '340px', marginBottom: '16px', lineHeight: '1.45' }}>
                {i18n.t('inventory.noSuppliersMatchHint')}
              </p>
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={onResetFilters}
                style={{ height: '36px', fontSize: '12px', padding: '0 16px', borderRadius: '6px', fontWeight: '600' }}
              >
                {i18n.t('inventory.resetSearchFilters')}
              </button>
            </div>
          ) : (
            <div className="table-section borderless-table-section">
              <div className="table-wrapper hidden md:block">
                <table className="leads-table">
                  <thead>
                    <tr>
                      <th>{i18n.t('inventory.tradeName')}</th>
                      <th>{i18n.t('inventory.vendorType')}</th>
                      <th>{i18n.t('inventory.vendorStatus')}</th>
                      <th>{i18n.t('inventory.riskLevelLabel')}</th>
                      <th style={{ width: '120px', textAlign: 'right' }}>{i18n.t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendors.map((vendor) => {
                      const isBlacklisted = vendor.status === 'Blacklisted';
                      
                      return (
                        <tr 
                          key={vendor.id} 
                          className="clickable-row"
                          onClick={() => onRowClick(vendor)}
                          style={{
                            backgroundColor: isBlacklisted ? '#fef2f2' : 'transparent',
                            transition: 'var(--transition-all)'
                          }}
                        >
                          <td style={{ fontWeight: '600', color: 'var(--text-dark)' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span>{vendor.tradeName}</span>
                            </div>
                          </td>
                          <td>
                            <span style={{ fontWeight: '500', color: 'var(--text-grey)' }}>
                              {vendor.type ? translateSupplierType(vendor.type) : i18n.t('inventory.emDash')}
                            </span>
                          </td>
                          <td>
                            <span 
                              style={{ 
                                display: 'inline-flex', 
                                padding: '4px 10px', 
                                borderRadius: '12px', 
                                fontSize: '11px', 
                                fontWeight: '700', 
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                backgroundColor: vendor.status === 'Active' ? '#ecfdf5' : isBlacklisted ? '#fee2e2' : '#f1f5f9',
                                color: vendor.status === 'Active' ? '#065f46' : isBlacklisted ? '#991b1b' : '#475569'
                              }}
                            >
                              {translateVendorStatus(vendor.status)}
                            </span>
                          </td>
                          <td>
                            <span 
                              style={{ 
                                display: 'inline-flex', 
                                padding: '4px 10px', 
                                borderRadius: '12px', 
                                fontSize: '11px', 
                                fontWeight: '700', 
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                backgroundColor: vendor.riskLevel === 'Low' ? '#e0f2fe' : vendor.riskLevel === 'Medium' ? '#fef3c7' : '#ffedd5',
                                color: vendor.riskLevel === 'Low' ? '#0369a1' : vendor.riskLevel === 'Medium' ? '#b45309' : '#c2410c'
                              }}
                            >
                              {translateRiskLevelLabel(vendor.riskLevel)}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <span
                                  style={{
                                  color: "var(--accent-blue)",
                                  fontWeight: "600",
                                  fontSize: "13px",
                                  cursor: "pointer",
                                }}
                                onClick={(e) => {
                                e.stopPropagation();
                                onEditVendor(vendor);
                              }}
                              >
                              {i18n.t('common.edit')}
                            </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden divide-y divide-[var(--color-text-border)]/35">
                {vendors.map((vendor) => (
                  <VendorMobileCard
                    key={vendor.id}
                    vendor={vendor}
                    onSelect={onRowClick}
                    onEdit={onEditVendor}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '16px 4px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-grey)' }}>
                    {i18n.t('notifications.page', { page: page + 1, total: totalPages })}
                    {totalElements != null
                      ? ` ${totalElements === 1
                          ? i18n.t('inventory.resultCountOne', { count: totalElements })
                          : i18n.t('inventory.resultCountMany', { count: totalElements })}`
                      : ''}
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={page <= 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      style={{ height: '36px', display: 'inline-flex', alignItems: 'center', gap: '4px', opacity: page <= 0 ? 0.5 : 1 }}
                    >
                      <ChevronLeft size={16} /> {i18n.t('inventory.prev')}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((p) => p + 1)}
                      style={{ height: '36px', display: 'inline-flex', alignItems: 'center', gap: '4px', opacity: page >= totalPages - 1 ? 0.5 : 1 }}
                    >
                      {i18n.t('pagination.next2')} <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
      </div>
    </AppLayout>
  );
}
